import { HttpClient, HttpParams } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  MasterDataCatalogDefinitionRequest,
  MasterDataCatalogDefinitionResponse,
  MasterDataCatalogItemRequest,
  MasterDataCatalogItemResponse,
  MasterDataCustomerRequest,
  MasterDataCustomerResponse,
  MasterDataCurrencyResponse,
  MasterDataCurrencyRequest,
  MasterDataExchangeRateResponse,
  MasterDataTaxUnitRequest,
  MasterDataTaxUnitResponse,
  MasterDataPensionFundRequest,
  MasterDataPensionFundResponse,
  MasterDataDepartmentResponse,
  MasterDataDistrictResponse,
  MasterDataDocumentTypeResponse,
  MasterDataDocumentTypeRequest,
  MasterDataProvinceResponse,
  MasterDataProductRequest,
  MasterDataProductResponse,
  MasterDataSctrRequest,
  MasterDataSctrResponse,
  MasterDataSctrType,
  MasterDataTaxRateRequest,
  MasterDataTaxRateResponse,
  MasterDataBankEntityRequest,
  MasterDataBankEntityResponse,
  MasterDataWorkerTypeRequest,
  MasterDataWorkerTypeResponse,
  MasterDataCountryResponse, MasterDataWorldLocationRequest, MasterDataWorldLocationResponse,
  MasterDataApiResponse,
  MasterDataSupplierPageRequest,
  MasterDataSupplierListRequest,
  MasterDataSupplierPageResponse,
  MasterDataSupplierRequest,
  MasterDataSupplierResponse,
  MasterDataSupplierSunatResponse,
  MasterDataSupplierRequirementPageRequest,
  MasterDataSupplierBankAccountPageRequest,
  MasterDataSupplierDetailPageResponse,
  MasterDataSupplierRequirementResponse,
  MasterDataSupplierBankAccountResponse,
  MasterDataSupportDocumentTypeResponse,
} from "../model/master-data.model";
import { BASE_PATH } from "../variables";

@Injectable({ providedIn: "root" })
export class StaffMasterDataService {
  private readonly apiUrl: string;

  constructor(
    private readonly http: HttpClient,
    @Inject(BASE_PATH) masterDataBasePath: string,
  ) {
    this.apiUrl = `${masterDataBasePath.replace(/\/$/, "")}/api/v1`;
  }

  listSuppliers(request: MasterDataSupplierPageRequest): Observable<MasterDataApiResponse<MasterDataSupplierPageResponse>> {
    const body = {
      page: request.page,
      size: request.size,
      ...(request.search?.trim() ? { search: request.search.trim() } : {}),
      ...(request.documentTypeId !== undefined ? { documentTypeId: request.documentTypeId } : {}),
      ...(request.countryId !== undefined ? { countryId: request.countryId } : {}),
      ...(request.supplierType ? { supplierType: request.supplierType } : {}),
    };
    return this.http.post<MasterDataApiResponse<MasterDataSupplierPageResponse>>(
      `${this.apiUrl}/suppliers/page`,
      body,
    );
  }

  getSuppliers(request: MasterDataSupplierListRequest = {}): Observable<MasterDataApiResponse<MasterDataSupplierResponse[]>> {
    let params = new HttpParams();
    if (request.active !== undefined) params = params.set("active", request.active);
    if (request.search?.trim()) params = params.set("search", request.search.trim());
    if (request.countryId !== undefined) params = params.set("countryId", request.countryId);
    return this.http.get<MasterDataApiResponse<MasterDataSupplierResponse[]>>(
      `${this.apiUrl}/suppliers`,
      { params },
    );
  }

  listSupportDocumentTypes(): Observable<MasterDataApiResponse<MasterDataSupportDocumentTypeResponse[]>> {
    return this.http.get<MasterDataApiResponse<MasterDataSupportDocumentTypeResponse[]>>(
      `${this.apiUrl}/support_document_types`,
    );
  }

  getSupplier(id: number): Observable<MasterDataApiResponse<MasterDataSupplierResponse>> {
    return this.http.get<MasterDataApiResponse<MasterDataSupplierResponse>>(
      `${this.apiUrl}/suppliers/${id}`,
    );
  }

  listSupplierRequirements(id: number, request: MasterDataSupplierRequirementPageRequest): Observable<MasterDataApiResponse<MasterDataSupplierDetailPageResponse<MasterDataSupplierRequirementResponse>>> {
    return this.http.post<MasterDataApiResponse<MasterDataSupplierDetailPageResponse<MasterDataSupplierRequirementResponse>>>(
      `${this.apiUrl}/suppliers/${id}/requirements/page`, request,
    );
  }

  listSupplierBankAccounts(id: number, request: MasterDataSupplierBankAccountPageRequest): Observable<MasterDataApiResponse<MasterDataSupplierDetailPageResponse<MasterDataSupplierBankAccountResponse>>> {
    return this.http.post<MasterDataApiResponse<MasterDataSupplierDetailPageResponse<MasterDataSupplierBankAccountResponse>>>(
      `${this.apiUrl}/suppliers/${id}/bank-accounts/page`, request,
    );
  }

  findSupplierByDocument(
    documentNumber: string,
  ): Observable<MasterDataApiResponse<MasterDataSupplierResponse>> {
    return this.http.get<MasterDataApiResponse<MasterDataSupplierResponse>>(
      `${this.apiUrl}/suppliers/document/${encodeURIComponent(documentNumber)}`,
    );
  }

  consultSupplierSunat(ruc: string): Observable<MasterDataApiResponse<MasterDataSupplierSunatResponse>> {
    return this.http.get<MasterDataApiResponse<MasterDataSupplierSunatResponse>>(
      `${this.apiUrl}/suppliers/sunat/${encodeURIComponent(ruc)}`,
    );
  }

  createSupplier(
    request: MasterDataSupplierRequest,
  ): Observable<MasterDataApiResponse<MasterDataSupplierResponse>> {
    return this.http.post<MasterDataApiResponse<MasterDataSupplierResponse>>(
      `${this.apiUrl}/suppliers`,
      request,
    );
  }

  updateSupplier(
    id: number,
    request: MasterDataSupplierRequest,
  ): Observable<MasterDataApiResponse<MasterDataSupplierResponse>> {
    return this.http.put<MasterDataApiResponse<MasterDataSupplierResponse>>(
      `${this.apiUrl}/suppliers/${id}`,
      request,
    );
  }

  deleteSupplier(id: number): Observable<MasterDataApiResponse<null>> {
    return this.http.delete<MasterDataApiResponse<null>>(
      `${this.apiUrl}/suppliers/${id}`,
    );
  }

  listDocumentTypes(): Observable<MasterDataDocumentTypeResponse[]> {
    return this.http.get<MasterDataDocumentTypeResponse[]>(
      `${this.apiUrl}/document_types`,
    );
  }

  createDocumentType(
    request: MasterDataDocumentTypeRequest,
  ): Observable<MasterDataDocumentTypeResponse> {
    return this.http.post<MasterDataDocumentTypeResponse>(
      `${this.apiUrl}/document_types`,
      request,
    );
  }

  updateDocumentType(
    id: number,
    request: MasterDataDocumentTypeRequest,
  ): Observable<MasterDataDocumentTypeResponse> {
    return this.http.put<MasterDataDocumentTypeResponse>(
      `${this.apiUrl}/document_types/${id}`,
      request,
    );
  }

  deleteDocumentType(id: number): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/document_types/${id}`,
    );
  }

  listCurrencies(activeOnly = true): Observable<MasterDataCurrencyResponse[]> {
    const params = activeOnly ? new HttpParams().set("active", true) : new HttpParams();
    return this.http.get<MasterDataCurrencyResponse[]>(
      `${this.apiUrl}/currencies`,
      { params },
    );
  }

  getLatestExchangeRate(
    companyId: number,
    currencyFromId: number,
    currencyToId: number,
  ): Observable<MasterDataExchangeRateResponse> {
    const params = new HttpParams()
      .set("companyId", companyId)
      .set("currencyFromId", currencyFromId)
      .set("currencyToId", currencyToId);
    return this.http.get<MasterDataExchangeRateResponse>(
      `${this.apiUrl}/exchange_rate/latest`,
      { params },
    );
  }

  createCurrency(
    request: MasterDataCurrencyRequest,
  ): Observable<MasterDataCurrencyResponse> {
    return this.http.post<MasterDataCurrencyResponse>(
      `${this.apiUrl}/currencies`,
      request,
    );
  }

  updateCurrency(
    idCurrency: number,
    request: MasterDataCurrencyRequest,
  ): Observable<MasterDataCurrencyResponse> {
    return this.http.put<MasterDataCurrencyResponse>(
      `${this.apiUrl}/currencies/${idCurrency}`,
      request,
    );
  }

  deleteCurrency(idCurrency: number | string): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/currencies/${idCurrency}`,
    );
  }

  listTaxUnits(activeOnly = true): Observable<MasterDataTaxUnitResponse[]> {
    return this.http.get<MasterDataTaxUnitResponse[]>(
      `${this.apiUrl}/tax-units`,
      { params: new HttpParams().set("activeOnly", activeOnly) },
    );
  }

  createTaxUnit(
    request: MasterDataTaxUnitRequest,
  ): Observable<MasterDataTaxUnitResponse> {
    return this.http.post<MasterDataTaxUnitResponse>(
      `${this.apiUrl}/tax-units`,
      request,
    );
  }

  updateTaxUnit(
    taxUnitId: number,
    request: MasterDataTaxUnitRequest,
  ): Observable<MasterDataTaxUnitResponse> {
    return this.http.put<MasterDataTaxUnitResponse>(
      `${this.apiUrl}/tax-units/${taxUnitId}`,
      request,
    );
  }

  deleteTaxUnit(taxUnitId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/tax-units/${taxUnitId}`);
  }

  listPensionFunds(
    activeOnly = true,
  ): Observable<MasterDataPensionFundResponse[]> {
    return this.http.get<MasterDataPensionFundResponse[]>(
      `${this.apiUrl}/pension-funds`,
      { params: new HttpParams().set("activeOnly", activeOnly) },
    );
  }

  createPensionFund(
    request: MasterDataPensionFundRequest,
  ): Observable<MasterDataPensionFundResponse> {
    return this.http.post<MasterDataPensionFundResponse>(
      `${this.apiUrl}/pension-funds`,
      request,
    );
  }

  updatePensionFund(
    pensionFundId: number,
    request: MasterDataPensionFundRequest,
  ): Observable<MasterDataPensionFundResponse> {
    return this.http.put<MasterDataPensionFundResponse>(
      `${this.apiUrl}/pension-funds/${pensionFundId}`,
      request,
    );
  }

  deletePensionFund(pensionFundId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/pension-funds/${pensionFundId}`,
    );
  }

  listDepartments(): Observable<MasterDataDepartmentResponse[]> {
    return this.http.get<MasterDataDepartmentResponse[]>(
      `${this.apiUrl}/locations/departments`,
    );
  }

  listProvinces(
    departmentCode: string,
  ): Observable<MasterDataProvinceResponse[]> {
    return this.http.get<MasterDataProvinceResponse[]>(
      `${this.apiUrl}/locations/departments/${encodeURIComponent(departmentCode)}/provinces`,
    );
  }

  listDistricts(
    provinceCode: string,
  ): Observable<MasterDataDistrictResponse[]> {
    return this.http.get<MasterDataDistrictResponse[]>(
      `${this.apiUrl}/locations/provinces/${encodeURIComponent(provinceCode)}/districts`,
    );
  }

  listTaxRates(
    activeOnly = true,
    code?: string,
  ): Observable<MasterDataTaxRateResponse[]> {
    let params = new HttpParams().set("activeOnly", activeOnly);
    if (code) params = params.set("code", code);
    return this.http.get<MasterDataTaxRateResponse[]>(`${this.apiUrl}/taxes`, {
      params,
    });
  }

  createTaxRate(
    request: MasterDataTaxRateRequest,
  ): Observable<MasterDataTaxRateResponse> {
    return this.http.post<MasterDataTaxRateResponse>(
      `${this.apiUrl}/taxes`,
      request,
    );
  }

  listCatalogs(
    activeOnly = true,
  ): Observable<MasterDataCatalogDefinitionResponse[]> {
    return this.http.get<MasterDataCatalogDefinitionResponse[]>(
      `${this.apiUrl}/catalogs`,
      { params: new HttpParams().set("activeOnly", activeOnly) },
    );
  }

  createCatalog(
    request: MasterDataCatalogDefinitionRequest,
  ): Observable<MasterDataCatalogDefinitionResponse> {
    return this.http.post<MasterDataCatalogDefinitionResponse>(
      `${this.apiUrl}/catalogs`,
      request,
    );
  }

  updateCatalog(
    catalogId: number,
    request: MasterDataCatalogDefinitionRequest,
  ): Observable<MasterDataCatalogDefinitionResponse> {
    return this.http.put<MasterDataCatalogDefinitionResponse>(
      `${this.apiUrl}/catalogs/${catalogId}`,
      request,
    );
  }

  listCatalogItems(
    catalogId: number,
    activeOnly = true,
  ): Observable<MasterDataCatalogItemResponse[]> {
    return this.http.get<MasterDataCatalogItemResponse[]>(
      `${this.apiUrl}/catalogs/${catalogId}/items`,
      { params: new HttpParams().set("activeOnly", activeOnly) },
    );
  }

  createCatalogItem(
    catalogId: number,
    request: MasterDataCatalogItemRequest,
  ): Observable<MasterDataCatalogItemResponse> {
    return this.http.post<MasterDataCatalogItemResponse>(
      `${this.apiUrl}/catalogs/${catalogId}/items`,
      request,
    );
  }

  updateCatalogItem(
    catalogId: number,
    itemId: number,
    request: MasterDataCatalogItemRequest,
  ): Observable<MasterDataCatalogItemResponse> {
    return this.http.put<MasterDataCatalogItemResponse>(
      `${this.apiUrl}/catalogs/${catalogId}/items/${itemId}`,
      request,
    );
  }

  listCustomers(
    companyId: number,
    activeOnly = true,
  ): Observable<MasterDataCustomerResponse[]> {
    const params = new HttpParams()
      .set("companyId", companyId)
      .set("activeOnly", activeOnly);
    return this.http.get<MasterDataCustomerResponse[]>(
      `${this.apiUrl}/customers`,
      { params },
    );
  }

  createCustomer(
    request: MasterDataCustomerRequest,
  ): Observable<MasterDataCustomerResponse> {
    return this.http.post<MasterDataCustomerResponse>(
      `${this.apiUrl}/customers`,
      request,
    );
  }

  updateCustomer(
    customerId: number,
    request: MasterDataCustomerRequest,
  ): Observable<MasterDataCustomerResponse> {
    return this.http.put<MasterDataCustomerResponse>(
      `${this.apiUrl}/customers/${customerId}`,
      request,
    );
  }

  deactivateCustomer(customerId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/customers/${customerId}`);
  }

  listProducts(
    companyId: number,
    activeOnly = true,
  ): Observable<MasterDataProductResponse[]> {
    const params = new HttpParams()
      .set("companyId", companyId)
      .set("activeOnly", activeOnly);
    return this.http.get<MasterDataProductResponse[]>(
      `${this.apiUrl}/products`,
      { params },
    );
  }

  createProduct(
    request: MasterDataProductRequest,
  ): Observable<MasterDataProductResponse> {
    return this.http.post<MasterDataProductResponse>(
      `${this.apiUrl}/products`,
      request,
    );
  }

  updateProduct(
    productId: number,
    request: MasterDataProductRequest,
  ): Observable<MasterDataProductResponse> {
    return this.http.put<MasterDataProductResponse>(
      `${this.apiUrl}/products/${productId}`,
      request,
    );
  }

  deactivateProduct(productId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/products/${productId}`);
  }

  listSctr(type?: MasterDataSctrType): Observable<MasterDataSctrResponse[]> {
    const params = type ? new HttpParams().set("type", type) : undefined;
    return this.http.get<MasterDataSctrResponse[]>(`${this.apiUrl}/sctr`, {
      params,
    });
  }

  createSctr(
    request: MasterDataSctrRequest,
  ): Observable<MasterDataSctrResponse> {
    return this.http.post<MasterDataSctrResponse>(
      `${this.apiUrl}/sctr`,
      request,
    );
  }

  updateSctr(
    sctrId: number,
    request: MasterDataSctrRequest,
  ): Observable<MasterDataSctrResponse> {
    return this.http.put<MasterDataSctrResponse>(
      `${this.apiUrl}/sctr/${sctrId}`,
      request,
    );
  }

  deleteSctr(sctrId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/sctr/${sctrId}`);
  }

  listBankEntities(): Observable<MasterDataBankEntityResponse[]> {
    return this.http.get<MasterDataBankEntityResponse[]>(
      `${this.apiUrl}/financial_institutions`,
    );
  }

  createBankEntity(
    request: MasterDataBankEntityRequest,
  ): Observable<MasterDataBankEntityResponse> {
    return this.http.post<MasterDataBankEntityResponse>(
      `${this.apiUrl}/financial_institutions`,
      request,
    );
  }

  updateBankEntity(
    bankEntityId: number,
    request: MasterDataBankEntityRequest,
  ): Observable<MasterDataBankEntityResponse> {
    return this.http.put<MasterDataBankEntityResponse>(
      `${this.apiUrl}/financial_institutions/${bankEntityId}`,
      request,
    );
  }

  deleteBankEntity(bankEntityId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/financial_institutions/${bankEntityId}`,
    );
  }

  listWorkerTypes(
    activeOnly = true,
  ): Observable<MasterDataWorkerTypeResponse[]> {
    return this.http.get<MasterDataWorkerTypeResponse[]>(
      `${this.apiUrl}/worker-types`,
      { params: new HttpParams().set("activeOnly", activeOnly) },
    );
  }

  createWorkerType(
    request: MasterDataWorkerTypeRequest,
  ): Observable<MasterDataWorkerTypeResponse> {
    return this.http.post<MasterDataWorkerTypeResponse>(
      `${this.apiUrl}/worker-types`,
      request,
    );
  }

  updateWorkerType(
    workerTypeId: number,
    request: MasterDataWorkerTypeRequest,
  ): Observable<MasterDataWorkerTypeResponse> {
    return this.http.put<MasterDataWorkerTypeResponse>(
      `${this.apiUrl}/worker-types/${workerTypeId}`,
      request,
    );
  }

  deleteWorkerType(workerTypeId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/worker-types/${workerTypeId}`,
    );
  }
  createCountry(request: any): Observable<MasterDataCountryResponse> {
    return this.http.post<MasterDataCountryResponse>(`${this.apiUrl}/countries`, request);
  }

  updateCountry(id: number, request: any): Observable<MasterDataCountryResponse> {
    return this.http.put<MasterDataCountryResponse>(`${this.apiUrl}/countries/${id}`, request);
  }

  deleteCountry(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/countries/${id}`);
  }

  listCountries(): Observable<MasterDataCountryResponse[]> {
    return this.http.get<MasterDataCountryResponse[]>(`${this.apiUrl}/countries`);
  }

  // World Locations
  listWorldLocations(countryCode?: string, regionCode?: string, provinceCode?: string): Observable<MasterDataWorldLocationResponse[]> {
    let params = new HttpParams();
    if (countryCode) params = params.set("countryCode", countryCode);
    if (regionCode) params = params.set("regionCode", regionCode);
    if (provinceCode) params = params.set("provinceCode", provinceCode);
    return this.http.get<MasterDataWorldLocationResponse[]>(`${this.apiUrl}/world_locations`, { params });
  }

  createWorldLocation(request: MasterDataWorldLocationRequest): Observable<MasterDataWorldLocationResponse> {
    return this.http.post<MasterDataWorldLocationResponse>(`${this.apiUrl}/world_locations`, request);
  }

  updateWorldLocation(id: number, request: MasterDataWorldLocationRequest): Observable<MasterDataWorldLocationResponse> {
    return this.http.put<MasterDataWorldLocationResponse>(`${this.apiUrl}/world_locations/${id}`, request);
  }

  deleteWorldLocation(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/world_locations/${id}`);
  }

  listRegions(countryCode: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/world_locations/regions`, { params: new HttpParams().set("countryCode", countryCode) });
  }

  listProvincesForWorldLocation(countryCode: string, regionCode: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/world_locations/provinces`, { 
      params: new HttpParams().set("countryCode", countryCode).set("regionCode", regionCode) 
    });
  }

  listDistrictsForWorldLocation(countryCode: string, regionCode: string, provinceCode: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/world_locations/districts`, { 
      params: new HttpParams().set("countryCode", countryCode).set("regionCode", regionCode).set("provinceCode", provinceCode) 
    });
  }
}

